import { FetchQueryOptions, QueryClient } from 'react-query';
import { UseQueryOptions } from 'react-query/types/react/types';

import { ApiViewSet, DetailOptions, apiDetail, apiList, useList, useRetrieve } from './baseApi';
import { ApiQueryParams } from './baseQueryParams';
import { Terminal } from './terminals';
import { FetchOptions } from './utils';

export interface SpotLayout {
    id: string;
    url: string;
    spot_url: string;
    spot: number | string;
    height: number;
    width: number;
    depth: number;
}

const defaultConfig = {
    staleTime: Infinity,
};

const spotLayoutViewSet: ApiViewSet = {
    baseName: 'spot-layouts',
};

enum SpotLayoutQueryParams {}

const listSpotLayouts = (queryParams?: ApiQueryParams<SpotLayoutQueryParams> | null, options?: FetchOptions): (() => Promise<SpotLayout[]>) => {
    return apiList<SpotLayout, SpotLayoutQueryParams>(spotLayoutViewSet, queryParams, options);
};

const fetchSpotLayout = (
    options: DetailOptions,
    queryParams?: ApiQueryParams<SpotLayoutQueryParams>,
    fetchOptions?: FetchOptions
): (() => Promise<SpotLayout>) => {
    return apiDetail({
        viewSet: spotLayoutViewSet,
        options,
        fetchOptions,
    });
};

export async function prefetchSpotLayout(queryClient: QueryClient, terminal: Terminal, options?: FetchQueryOptions<SpotLayout>, fetchOptions?: FetchOptions) {
    const config = {
        ...defaultConfig,
        ...options,
    };

    const spotLayout = await queryClient.fetchQuery(
        ['spot-layout', undefined, { url: terminal.spot_layout_url }],
        fetchSpotLayout(
            {
                url: terminal.spot_layout_url,
            },
            undefined,
            fetchOptions
        ),
        config
    );
    // Set the spot layout detail caches as well
    queryClient.setQueryData(['spot-layout', spotLayout.id, undefined], spotLayout);
    if (spotLayout.url !== terminal.spot_layout_url) {
        queryClient.setQueryData(['spot-layout', undefined, { url: terminal.spot_layout_url }], spotLayout);
    }

    return spotLayout;
}

export function useSpotLayout(spotLayoutUrl?: string, spotLayoutId?: number | string, options?: UseQueryOptions<SpotLayout>, fetchOptions?: FetchOptions) {
    return useRetrieve({
        viewSet: spotLayoutViewSet,
        queryParamsEnum: SpotLayoutQueryParams,
        fetchFn: fetchSpotLayout,
        detail: spotLayoutUrl ? { url: spotLayoutUrl } : spotLayoutId ? { id: spotLayoutId } : undefined,
        fetchOptions: fetchOptions,
    });
}

export function useSpotLayouts(options?: UseQueryOptions<SpotLayout[]>, fetchOptions?: FetchOptions) {
    return useList({
        viewSet: spotLayoutViewSet,
        queryParamsEnum: SpotLayoutQueryParams,
        fetchFn: listSpotLayouts,
        queryOptions: options,
        fetchOptions: fetchOptions,
    });
}
